import React, { useState, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '../bootstrap/Button';
import Icon from '../icon/Icon';
import AuthContext from '../../contexts/authContext';
import { UserType } from '../../common/data/dummyRolesData';
import subscriptionService from '../../services/api/subscriptionService';

// Lazy import to avoid circular dependency
let useOrganization: () => { selectedOrganization: { id: string } | null };
try {
	useOrganization = require('../../contexts/organizationContext').useOrganization;
} catch {
	useOrganization = () => ({ selectedOrganization: null });
}

interface LockedItemOverlayProps {
	/** Feature code for analytics/tracking */
	featureCode?: string;
	/** Feature display name */
	featureName?: string;
	/** Callback when overlay is closed */
	onClose: () => void;
}

/**
 * Modal overlay shown when a locked menu item is clicked.
 * Provides upgrade prompt and redirect to Stripe Portal.
 */
/**
 * Full-screen overlay shown when a user interacts with a locked/gated item.
 * Displays a modal-like dialog with an upgrade CTA that redirects to Stripe portal.
 *
 * @userstory US-FG-02, US-FG-10
 */
const LockedItemOverlay: React.FC<LockedItemOverlayProps> = ({
	featureCode,
	featureName,
	onClose,
}) => {
	const { t } = useTranslation(['features', 'common']);
	const navigate = useNavigate();
	const { userData } = useContext(AuthContext);
	const [redirecting, setRedirecting] = useState(false);

	// Try to get organization context (only available in company pages)
	let organizationId: string | null = null;
	try {
		const { selectedOrganization } = useOrganization();
		organizationId = selectedOrganization?.id || null;
	} catch {
		organizationId = null;
	}

	const handleManageSubscription = useCallback(async () => {
		const isCompany = userData?.role === UserType.COMPANY;
		const isFreelance = userData?.role === UserType.FREELANCE;

		try {
			setRedirecting(true);

			if (isFreelance) {
				// Navigate to talent subscription page
				onClose();
				navigate('/talent/settings/subscription');
			} else if (isCompany && organizationId) {
				await subscriptionService.redirectToOrganizationPortal(organizationId);
			} else {
				// Fallback: navigate to subscription page
				onClose();
				navigate('/company/subscription');
			}
		} catch (err) {
			console.error('Failed to redirect to portal:', err);
			setRedirecting(false);
			// Fallback to subscription page on error
			onClose();
			const fallbackPath = userData?.role === UserType.FREELANCE ? '/talent/settings/subscription' : '/company/subscription';
			navigate(fallbackPath);
		}
	}, [userData, organizationId, navigate, onClose]);

	return (
		<div
			className='locked-item-overlay position-fixed'
			style={{
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				backgroundColor: 'rgba(0, 0, 0, 0.5)',
				zIndex: 1050,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			}}
			onClick={onClose}
			role='dialog'
			aria-modal='true'
			aria-labelledby='locked-item-title'>
			<div
				className='locked-item-modal bg-white rounded p-4'
				style={{ maxWidth: '400px', width: '90%' }}
				onClick={(e) => e.stopPropagation()}>
				<div className='text-center'>
					<Icon icon='Lock' size='3x' className='text-warning mb-3' />
					<h5 id='locked-item-title'>{t('subscription.lockedItem.title')}</h5>
					<p className='text-muted'>
						{t('subscription.lockedItem.description', {
							feature: featureName || t('features.upgrade.thisFeature'),
						})}
					</p>
					<div className='d-flex gap-2 justify-content-center mt-4'>
						<Button color='light' onClick={onClose}>
							{t('Cancel')}
						</Button>
						<Button
							color='primary'
							onClick={handleManageSubscription}
							isDisable={redirecting}>
							{redirecting ? (
								<>
									<span className='spinner-border spinner-border-sm me-2' />
									{t('subscription.redirecting')}
								</>
							) : (
								<>
									<Icon icon='OpenInNew' className='me-2' />
									{t('subscription.lockedItem.cta')}
								</>
							)}
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default LockedItemOverlay;
